import { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// 1. Тип для тела ответа хука useQuery, который будет передаваться
// D - тип элемента в массиве данных
// K - ключ, по которому находится массив (например, 'conversations', 'products', 'users')
export type PaginatedResponse<D, K extends string> = {
  total: number;
} & {
  [P in K]: D[];
};

// 2. Тип для хука-обертки над useQuery, который будет передаваться в универсальный хук
// P - тип параметров (props) для этого хука
// D - тип элемента в массиве данных
// K - ключ, по которому находится массив
export type PaginatedQueryHook<P, D, K extends string> = (
  params: P & {
    limit: number;
    offset: number;
  },
) => UseQueryResult<PaginatedResponse<D, K>>;

// 3. Универсальный хук
export const usePaginatedQuery = <
  // P: Тип параметров, которые можно передать в QueryHook (кроме limit/offset)
  P extends object,
  // D: Тип элемента в массиве данных
  D,
  // K: Ключ, по которому находится массив элементов в ответе (например, 'conversations')
  K extends string,
>(
  // queryHook: Хук, который оборачивает useQuery и принимает параметры, limit и offset
  queryHook: PaginatedQueryHook<P, D, K>,
  // itemsKey: Фактический ключ массива данных (например, 'conversations')
  itemsKey: K,
  // params: Дополнительные параметры для запроса (например, isDeleted)
  params?: P,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Инициализация пагинации из URL
  const [page, setPage] = useState(() => +(searchParams.get('page') || 1));
  const [perPage, setPerPage] = useState(() => +(searchParams.get('perPage') || 50));

  // Вызов переданного хука с параметрами пагинации
  const query = queryHook({
    ...(params as P), // Утверждаем тип для безопасности
    limit: perPage,
    offset: (page - 1) * perPage,
  });

  const changePagination = (nextPage: number, nextPerPage: number) => {
    setPerPage((prev) => {
      const finalPage = prev !== nextPerPage ? 1 : nextPage;
      setSearchParams((prev) => {
        prev.set('perPage', nextPerPage.toString());
        prev.set('page', finalPage.toString());
        return prev;
      });
      setPage(finalPage);
      return nextPerPage;
    });
  };

  // Вывод: комбинирование результата useQuery, функций-манипуляторов и текущего состояния пагинации
  return {
    ...query,
    changePagination,
    page,
    perPage,
    // Удобный доступ к массиву элементов через переданный ключ
    // data: { total: number, conversations: Item[] } -> items: Item[] | undefined
    items: query.data ? query.data[itemsKey] : undefined,
  };
};
